import { useCallback, useRef, useState } from "react";
import { assessStake } from "@/lib/stakeGuard";

/* 🛡 shared stake-guard — pauses a real-money play when the stake is risky
   for the balance; resolves with the stake the user chose to continue with.
   The same stake value is never re-asked twice in a session. */
export const useStakeGuard = ({ isReal, balanceRef }) => {
  const [guard, setGuard] = useState(null);
  const ackRef = useRef(0);
  const guardRef = useRef(null);
  const check = useCallback((amt, opts = {}) => {
    if (!isReal) return Promise.resolve(amt);
    const bal = Number(balanceRef.current) || 0;
    if (bal <= 0) return Promise.resolve(amt);
    if (ackRef.current === amt) return Promise.resolve(amt);
    const assess = assessStake({ stake: amt, balance: bal, ...opts });
    if (!assess.risky) return Promise.resolve(amt);
    return new Promise((resolve) => {
      guardRef.current?.resolve?.(null); // a superseded play is CANCELLED, never auto-fired
      const g = { stake: amt, assess, resolve };
      guardRef.current = g;
      setGuard(g);
    });
  }, [isReal, balanceRef]);
  const decide = useCallback((value) => {
    const g = guardRef.current;
    if (!g) return;
    ackRef.current = value;
    guardRef.current = null;
    setGuard(null);
    g.resolve(value);
  }, []);
  return { guard, check, decide };
};
